import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

interface AttachmentPreviewProps {
	file: File;
	uploadProgress?: number;
	isComplete?: boolean;
	hasError?: boolean;
	onRemove: () => void;
	canRemove?: boolean;
}

export const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
	file,
	uploadProgress = 0,
	isComplete = false,
	hasError = false,
	onRemove,
	canRemove = true,
}) => {
	const [preview, setPreview] = useState<string | null>(null);
	const isImage = file.type.startsWith('image/');

	// Generate preview for images
	useEffect(() => {
		if (isImage) {
			const objectUrl = URL.createObjectURL(file);
			setPreview(objectUrl);

			// Cleanup on unmount
			return () => URL.revokeObjectURL(objectUrl);
		}
	}, [file, isImage]);

	const getStatusText = () => {
		if (hasError) return 'Error';
		if (isComplete) return 'Ready';
		if (uploadProgress > 0) return `${uploadProgress}%`;
		return '';
	};

	const getStatusColor = () => {
		if (hasError) return 'error.main';
		if (isComplete) return 'success.main';
		return 'text.secondary';
	};

	if (isImage && preview) {
		return (
			<Box
				sx={{
					position: 'relative',
					border: '1px solid #e0e0e0',
					borderRadius: '8px',
					overflow: 'hidden',
					maxWidth: '120px',
					backgroundColor: hasError ? 'rgba(211, 47, 47, 0.1)' : 'inherit',
				}}
			>
				<img
					src={preview}
					alt={file.name}
					style={{
						width: '100%',
						height: '80px',
						objectFit: 'cover',
						display: 'block',
					}}
				/>

				{/* Remove button */}
				{canRemove && (
					<IconButton
						size="small"
						onClick={onRemove}
						sx={{
							position: 'absolute',
							top: 4,
							right: 4,
							backgroundColor: 'rgba(0,0,0,0.6)',
							color: 'white',
							width: 24,
							height: 24,
							border: '1px solid rgba(255,255,255,0.3)',
							'&:hover': {
								backgroundColor: 'rgba(0,0,0,0.8)',
								transform: 'scale(1.1)',
								transition: 'all 0.2s ease',
							},
						}}
						title="Remove attachment"
					>
						<Icon path={mdiClose} size={0.5} />
					</IconButton>
				)}

				{/* Status overlay */}
				{(uploadProgress > 0 || isComplete || hasError) && (
					<Box
						sx={{
							position: 'absolute',
							bottom: 0,
							left: 0,
							right: 0,
							backgroundColor: 'rgba(0,0,0,0.7)',
							color: 'white',
							padding: '2px 8px',
							textAlign: 'center',
						}}
					>
						<Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
							{getStatusText()}
						</Typography>
					</Box>
				)}
			</Box>
		);
	}

	// Non-image file preview
	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				padding: '8px 12px',
				border: '1px solid #e0e0e0',
				borderRadius: '8px',
				backgroundColor: hasError ? 'rgba(211, 47, 47, 0.1)' : 'inherit',
				maxWidth: '200px',
			}}
		>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
					{file.name}
				</Typography>

				{(uploadProgress > 0 || isComplete || hasError) && (
					<Typography variant="caption" sx={{ color: getStatusColor() }}>
						{getStatusText()}
					</Typography>
				)}
			</Box>

			{canRemove && (
				<IconButton 
					size="small" 
					onClick={onRemove} 
					sx={{ 
						padding: '4px',
						'&:hover': {
							backgroundColor: 'rgba(0,0,0,0.1)',
							transform: 'scale(1.1)',
							transition: 'all 0.2s ease',
						},
					}}
					title="Remove attachment"
				>
					<Icon path={mdiClose} size={0.6} />
				</IconButton>
			)}
		</Box>
	);
};
